<template>
  <el-dialog
    class="dialog-wrapper values-sym-lot"
    visible
    show-close
    :close-on-click-modal="false"
    @close="abortInsert"
  >
    <ContentHeader
      slot="title"
      :title="$t('analysesData.valuesSymLotBulk.title')"
    />
    <div v-if="!apiCallInProgress">
      <el-row :gutter="20">
        <el-col :span="24">
          <el-steps
            :active="stepActive"
            finish-status="success"
            :align-center="true"
          >
            <el-step
              :title="$t('analysesData.valuesSymLotBulk.step1')"
            ></el-step>
            <el-step
              :title="$t('analysesData.valuesSymLotBulk.step2')"
            ></el-step>
            <el-step
              :title="$t('analysesData.valuesSymLotBulk.step3')"
            ></el-step>
            <el-step
              :title="$t('analysesData.valuesSymLotBulk.step4')"
            ></el-step>
          </el-steps>
        </el-col>
      </el-row>
      <el-divider />
      <ValidationObserver
        ref="valuesTemperatureDataForm"
        v-slot="{ handleSubmit, invalid }"
      >
        <el-form
          ref="form"
          @submit.native.prevent
          label-position="top"
        >
          <!-- Step 1 -->
          <el-row
            v-if="stepActive === 0"
            :gutter="20"
          >
            <el-col
              :span="10"
              :offset="7"
            >
              <FormItem :label="$t('analysesData.valuesSymLotBulk.groupingId')">
                <el-input
                  ref="QRGrouping"
                  @keyup.enter.native="opValuesMixin.searchGrouping"
                  v-model="grouping.code"
                />
                <label class="el-form-item__label">
                  {{ $t('analysesData.valuesSymLotBulk.qrScanExplanation') }}
                </label>
              </FormItem>
            </el-col>
            <el-col :span="24">
              <div class="table-wrapper">
                <el-table
                  class="width-100"
                  :data="sampleGroupingsTableData"
                  border
                >
                  <el-table-column
                    prop="groupingId"
                    :label="$t('filters.grouping')"
                  />
                  <el-table-column
                    prop="samplesCount"
                    :label="$t('filters.samplesCount')"
                    :min-width="30"
                  />
                  <el-table-column
                    :min-width="10"
                    align="center"
                  >
                    <template slot-scope="scope">
                      <span
                        :style="{ cursor: 'pointer' }"
                        @click="opValuesMixin.removeGrouping(scope.row)"
                      >
                        <i class="state-icon fas fa-trash" />
                      </span>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-col>
          </el-row>
          <!-- End Step 1 -->

          <!-- Step 2 -->
          <el-row
            v-if="stepActive === 1"
            :gutter="20"
          >
            <div
              v-if="!analysesValues.length && !loading"
              class="no-operation"
            >
              {{ $t('analysesData.valuesSymLotBulk.noOperation') }}
            </div>
            <div v-else>
              <el-row
                :gutter="20"
                type="flex"
              >
                <el-col :span="24">
                  <div class="table-wrapper">
                    <el-table
                      ref="analysesTable"
                      v-loading="loading"
                      element-loading-background="rgba(48,53,82,0.9)"
                      class="width-100"
                      :data="analysesValues"
                      border
                      @selection-change="handleSelectionChange"
                    >
                      <el-table-column
                        type="selection"
                        width="40"
                      />
                      <el-table-column
                        prop="analysisCode"
                        :label="
                          $t('analysesData.valuesSymLotBulk.analysisCode')
                        "
                      />
                      <el-table-column
                        prop="analysisDescription"
                        :label="
                          $t(
                            'analysesData.valuesSymLotBulk.analysisDescription'
                          )
                        "
                      />
                    </el-table>
                  </div>
                </el-col>
              </el-row>
            </div>
          </el-row>
          <!-- End Step 2 -->

          <!-- Step 3 -->
          <el-row
            v-if="stepActive === 2"
            :gutter="20"
          >
            <div
              v-if="!Object.keys(operationsList).length"
              class="no-operation"
            >
              {{ $t('analysesData.valuesSymLotBulk.noOperation') }}
            </div>
            <div v-else>
              <div
                v-for="key in Object.keys(operationsList)"
                :key="key"
              >
                <SymbolLotBulkEdit
                  v-model="operationsList[key]"
                  :card-key="key"
                  :is-open="openCollapseStep3 === key"
                  @toggle-collapse="handleCollapseToggleStep3"
                />
              </div>
            </div>
          </el-row>
          <!-- End Step 3 -->

          <!-- Step 4 -->
          <el-row
            v-if="stepActive === 3"
            :gutter="20"
          >
            <div
              v-if="!Object.keys(sampleList).length"
              class="no-operation"
            >
              {{ $t('analysesData.valuesSymLotBulk.noSample') }}
            </div>
            <div v-else>
              <div
                v-for="key in Object.keys(sampleList)"
                :key="key"
              >
                <SampleSymLotBulk
                  v-model="sampleList[key]"
                  :card-key="key"
                  :is-open="openCollapseStep4 === key"
                  @toggle-collapse="handleCollapseToggleStep4"
                />
              </div>
            </div>
          </el-row>
          <!-- End Step 4 -->

          <FormButtons>
            <el-button
              type="secondary"
              @click.prevent="abortInsert"
            >
              {{ $t('global.cancel') }}
            </el-button>
            <el-button
              type="secondary"
              v-if="stepActive > 0 && stepActive < 4"
              @click.prevent="prevStep"
            >
              {{ $t('global.prev') }}
            </el-button>
            <el-button
              type="primary"
              :disabled="invalid"
              v-if="stepActive < 3"
              @click.prevent="nextStep"
            >
              {{ $t('global.next') }}
            </el-button>
            <el-button
              type="primary"
              :disabled="
                invalid ||
                (stepActive === 3 &&
                  !operationsFilteredByAnalyses.length &&
                  !sampleList.length)
              "
              @click.prevent="handleSubmit(insertValues)"
              v-if="stepActive === 3"
            >
              {{ $t('global.confirm') }}
            </el-button>
          </FormButtons>
        </el-form>
      </ValidationObserver>
    </div>

    <BulkCall
      ref="refBulk"
      :function="insertValue"
      :arguments="operationValuesArr"
      @completed="bulkCompleted"
    />
  </el-dialog>
</template>

<script>
  import groupBy from 'lodash.groupby';
  import { analysesDataApi } from '@/api';
  import { SymbolTypes } from '@/config/constants';

  import insertSymLotBulk from '@/mixins/insertSymLotBulk';

  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import BulkCall from '@/components/BulkCall.vue';
  import SymbolLotBulkEdit from './SymbolLotBulkEdit.vue';
  import SampleSymLotBulk from './SampleSymLotBulk.vue';

  export default {
    name: 'InsertSymLotBulk',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      BulkCall,
      SymbolLotBulkEdit,
      SampleSymLotBulk,
    },
    data() {
      return {
        stepActive: 0,
        grouping: {
          code: null,
        },
        sampleIdsWithGrouping: {},
        selectedAnalyses: {},
        operationValuesGrouped: {},
        loading: false,
        apiCallInProgress: false,

        symLotData: [],
        analysesValues: [],
        analysesIds: [],
        operationsFilteredByAnalyses: [],
        operationsList: [],
        sampleList: [],

        openCollapseStep3: null,
        openCollapseStep4: null,
      };
    },
    computed: {
      sampleGroupingsTableData() {
        return Object.keys(this.sampleIdsWithGrouping).map((key) => ({
          groupingId: key,
          samplesCount: this.sampleIdsWithGrouping[key].length,
        }));
      },
      // selectedAnalysesTableData() {
      //   return Object.keys(this.selectedAnalyses).map((key) => (
      //     {
      //       id: key,
      //       ...this.selectedAnalyses[key],
      //     }
      //   ));
      // },
      // sampleIdsFlattened() {
      //   return Array.from(new Set(Object.keys(this.sampleIdsWithGrouping)
      //     .flatMap((key) => this.sampleIdsWithGrouping[key].map((s) => s.id))));
      // },
      operationValuesArr() {
        const operations = this.operationsFilteredByAnalyses.map(
          (operation) => ({
            labSampleAnalysisId: operation.labSampleAnalysisId,
            operationRegistryId: operation.operationRegistryId,
            symbol: operation.symbol,
            symbolLot: operation.symbolLot,
          })
        );
        return Object.values(groupBy(operations, 'labSampleAnalysisId')).map(
          (op) => [op]
        );
      },
      symbolTypes() {
        return Object.values(SymbolTypes).map((val) => ({
          value: val,
          label: this.$t(`enums.symbolTypes.${val}`),
        }));
      },
    },
    created() {
      this.opValuesMixin = insertSymLotBulk(this);
    },
    mounted() {
      this.$nextTick(() => {
        this.$refs.QRGrouping.focus();
      });
    },
    methods: {
      prevStep() {
        this.stepActive -= 1;
        if (this.stepActive < 0) {
          this.stepActive = 0;
        }
        this.focusInputs();
      },
      async nextStep() {
        this.stepActive += 1;
        if (this.stepActive === 1) {
          this.loading = true;
          await this.opValuesMixin.lotToOperationList();
          await this.opValuesMixin.getAnalysesValues();
          this.loading = false;
          if (this.analysesValues.length) {
            await this.$nextTick();
            this.analysesValues.forEach((analysis) => {
              this.$refs.analysesTable.toggleRowSelection(analysis);
              if (!this.analysesIds.includes(analysis.analysisId)) {
                this.analysesIds.push(analysis.analysisId);
              }
            });
          }
        }
        if (this.stepActive === 2) this.opValuesMixin.getOperationsList();
        if (this.stepActive === 3) this.opValuesMixin.getSamplesList();
        if (this.stepActive > 3) {
          this.stepActive = 3;
        }
        this.focusInputs();
      },
      focusInputs() {
        this.$nextTick(() => {
          if (this.$refs.QRGrouping) this.$refs.QRGrouping.focus();
          if (this.$refs.QRAnalysis) this.$refs.QRAnalysis.focus();
        });
      },
      handleCollapseToggleStep3(cardKey, isOpen) {
        this.openCollapseStep3 = isOpen ? cardKey : null;
      },
      handleCollapseToggleStep4(cardKey, isOpen) {
        this.openCollapseStep4 = isOpen ? cardKey : null;
      },
      insertValue(operationValue) {
        return analysesDataApi.addSymbolLot(operationValue);
      },
      insertValues() {
        this.apiCallInProgress = true;
        this.$refs.refBulk.run();
      },
      bulkCompleted(errors) {
        if (!errors) {
          this.$emit('dialogClose', { success: true });
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('global.success'),
          });
        }
      },
      abortInsert() {
        this.$emit('dialogClose', { success: this.apiCallInProgress });
      },
      handleSelectionChange(selection) {
        const ids = selection.map((el) => el.analysisId);
        this.analysesIds = ids;
      },
    },
  };
</script>

<style lang="scss">
  .values-sym-lot {
    .el-dialog {
      width: 50%;
    }

    .el-form-item__label {
      word-break: initial;
      line-height: 1.4;
    }
    .no-operation {
      text-align: center;
    }
  }
</style>
